import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ImageOff, LogIn } from "lucide-react";

/**
 * Public product-scan landing page.
 *
 * The QR code printed on every product label opens this route. The page fetches
 * ``GET /api/scan/{itemNumber}`` which returns role-tailored data (see the
 * backend ``scan.py`` docstring for the shape shown to Sales / Buyer / Anon).
 *
 * We deliberately DO NOT require login here — anonymous scans get a padded
 * 1.1× price so field scans don't leak the exact quoted price to end customers.
 *
 * 2026-02-27 · The QR URL carries a ``?c=USD|CAD`` param baked in by
 * ``export_quotes.py::_product_qr_url`` — we MUST forward it to the backend
 * so the scan page's currency matches the printed label's currency.
 */
export default function ScanProduct() {
  const { itemNumber } = useParams();
  const [search] = useSearchParams();
  const currencyParam = (search.get("c") || "").toUpperCase();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    // ``withCredentials`` so a logged-in staff scanner's cookie is sent along —
    // that's what lets the backend pick between the sales / buyer view.
    const params = {};
    if (currencyParam === "USD" || currencyParam === "CAD") {
      params.c = currencyParam;
    }
    axios
      .get(`${backend}/api/scan/${encodeURIComponent(itemNumber)}`, { params, withCredentials: true })
      .then((res) => alive && setState({ loading: false, data: res.data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: e.response?.data?.detail || "load_failed" }));
    return () => { alive = false; };
  }, [itemNumber, currencyParam]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 text-neutral-400 text-sm">
        Loading…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 p-6">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-neutral-700 dark:text-neutral-200">Product not found</p>
          <p className="text-xs text-neutral-500 font-mono">{itemNumber}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const isBuyer = d.view === "buyer";
  const isAnon = d.role === "anon";
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const photoSrc = d.photo_url ? (d.photo_url.startsWith("http") ? d.photo_url : `${backend}${d.photo_url}`) : null;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100" data-testid="scan-page">
      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        {/* Item number badge */}
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-bold tracking-wider text-neutral-500" data-testid="scan-item-number">
            {d.item_number}
          </span>
          {isBuyer ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 font-semibold">采购视图</span>
          ) : isAnon ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 font-semibold">Public View</span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-semibold">Sales View</span>
          )}
        </div>

        {/* Photo */}
        <div className="rounded-xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 aspect-square flex items-center justify-center">
          {photoSrc ? (
            <img src={photoSrc} alt={d.item_number || "product"} className="w-full h-full object-contain"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="scan-photo" />
          ) : (
            <ImageOff className="w-12 h-12 text-neutral-300" />
          )}
        </div>

        {/* Description + Specification */}
        <div className="space-y-3 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">
              {isBuyer ? "产品描述" : "Description"}
            </p>
            <p className="text-sm leading-snug" data-testid="scan-description">{d.description || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">
              {isBuyer ? "产品规格" : "Specification"}
            </p>
            <p className="text-sm leading-snug text-neutral-700 dark:text-neutral-300" data-testid="scan-specification">
              {d.specification || "—"}
            </p>
          </div>
        </div>

        {/* Pricing table */}
        {(d.price_rows || []).length > 0 && (
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden" data-testid="scan-price-table">
            <div className="grid grid-cols-[1.4fr_0.7fr_1fr] px-4 py-2 text-[10px] uppercase tracking-wider text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
              <span>Size</span>
              <span className="text-right">PCS/CTN</span>
              <span className="text-right">{isBuyer ? "Factory Cost" : "Price"}</span>
            </div>
            {d.price_rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-[1.4fr_0.7fr_1fr] px-4 py-2.5 text-sm border-b border-neutral-100 dark:border-neutral-800/60 last:border-0" data-testid={`scan-price-row-${idx}`}>
                <span className="truncate">{r.size || "—"}</span>
                <span className="text-right tabular-nums text-neutral-600 dark:text-neutral-400">
                  {r.qty_per_ctn ? Math.round(r.qty_per_ctn) : "—"}
                </span>
                <span className="text-right tabular-nums font-semibold">
                  {isBuyer ? formatBuyerCost(r) : formatSalesPrice(r.price, d.currency)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Anon call-to-action: prompt the visitor to log in for the accurate
            (non-1.1×-padded) quote price. Deep-links back to this scan page after
            successful auth via the ``next`` query param handled by Login.jsx. */}
        {isAnon && (
          <div className="pt-2 space-y-3" data-testid="scan-anon-cta">
            <Link
              to={`/login?next=${encodeURIComponent(`/scan/${itemNumber}`)}`}
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 py-3 text-sm font-semibold shadow-sm active:scale-[0.98] transition"
              data-testid="scan-login-button"
            >
              <LogIn className="w-4 h-4" />
              <span>注册 / 登录</span>
              <span className="text-white/60 dark:text-neutral-500 text-xs ml-1">· Register / Log In</span>
            </Link>
            <p className="text-[10px] text-neutral-400 text-center italic" data-testid="scan-anon-notice">
              Retail preview — log in for the exact quoted price.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatSalesPrice(v, currency = "USD") {
  if (v == null || v === 0 || Number.isNaN(Number(v))) return "—";
  const prefix = currency === "CAD" ? "C$" : "$";
  return `${prefix}${Number(v).toFixed(3)}`;
}

function formatBuyerCost(r) {
  const usd = Number(r.factory_cost_usd || 0);
  const raw = Number(r.factory_cost || 0);
  const cur = (r.cost_currency || "").toUpperCase();
  if (cur === "USD" && usd) return `$${usd.toFixed(3)}`;
  if (raw) return `${cur === "USD" ? "$" : (cur ? cur + " " : "¥")}${raw.toFixed(3)}`;
  return "—";
}
